import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';

const EMICalculator = () => {
  const [principal, setPrincipal] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [tenure, setTenure] = useState('');
  const [emi, setEmi] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  const calculateEMI = () => {
    const P = parseFloat(principal);
    const R = parseFloat(interestRate) / 12 / 100; // Monthly interest rate
    const N = parseFloat(tenure); // Total number of months

    if (P > 0 && R >= 0 && N > 0) {
      const emiValue = (P * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
      const totalAmountValue = emiValue * N;
      const totalInterestValue = totalAmountValue - P;

      setEmi(Math.round(emiValue));
      setTotalAmount(Math.round(totalAmountValue));
      setTotalInterest(Math.round(totalInterestValue));
    } else {
      setEmi(0);
      setTotalAmount(0);
      setTotalInterest(0);
    }
  };

  useEffect(() => {
    calculateEMI();
  }, [principal, interestRate, tenure]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const copyResult = () => {
    const resultText = `EMI Calculation:
Principal: ${formatCurrency(parseFloat(principal) || 0)}
Interest Rate: ${interestRate}% p.a.
Tenure: ${tenure} months
EMI: ${formatCurrency(emi)}
Total Amount: ${formatCurrency(totalAmount)}
Total Interest: ${formatCurrency(totalInterest)}`;
    
    navigator.clipboard.writeText(resultText);
    alert('Results copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="EMI Calculator – Easy Loan EMI Calculation"
        description="Calculate EMI for any loan with our easy-to-use calculator. Get instant EMI calculations for personal loans, car loans, home loans, and more. Free online EMI calculator tool."
        keywords="EMI calculator, loan EMI, finance tools, personal loan calculator, car loan calculator, home loan EMI, loan calculator India"
        type="webapplication"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Calculators', url: '/calculators' },
          { name: 'EMI Calculator', url: '/calculators/emi' }
        ]}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'EMI Calculator',
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'INR'
          },
          description: 'Free online EMI calculator for calculating Equated Monthly Installments for any loan type'
        }}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'EMI Calculator', url: '/calculators/emi' }
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            EMI Calculator
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Calculate EMI for any loan instantly. Perfect for personal loans, car loans, and other financing needs.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="card">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Loan Details
            </h2>
            
            <div className="space-y-6">
              {/* Principal Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loan Amount (Principal)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                  <input
                    type="number"
                    value={principal}
                    onChange={(e) => setPrincipal(e.target.value)}
                    placeholder="Enter loan amount"
                    className="input-field pl-8"
                  />
                </div>
              </div>

              {/* Interest Rate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interest Rate (% per annum)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value)}
                    placeholder="Enter interest rate"
                    className="input-field pr-8"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                </div>
              </div>

              {/* Tenure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loan Tenure (Months)
                </label>
                <input
                  type="number"
                  value={tenure}
                  onChange={(e) => setTenure(e.target.value)}
                  placeholder="Enter loan tenure in months"
                  className="input-field"
                />
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {/* EMI Result */}
            <div className="result-card">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Monthly EMI</h3>
                <div className="text-4xl font-bold text-primary-600 mb-2">
                  {formatCurrency(emi)}
                </div>
                <p className="text-sm text-gray-600">
                  Equated Monthly Installment
                </p>
              </div>
            </div>

            {/* Detailed Results */}
            <div className="card">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Loan Summary
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Principal Amount</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(parseFloat(principal) || 0)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-600">Total Interest</span>
                  <span className="font-semibold text-red-600">
                    {formatCurrency(totalInterest)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-semibold">Total Amount</span>
                  <span className="font-bold text-primary-600 text-lg">
                    {formatCurrency(totalAmount)}
                  </span>
                </div>
              </div>

              <button
                onClick={copyResult}
                className="w-full mt-6 btn-primary"
              >
                Copy Results
              </button>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              💡 EMI Tips
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Choose shorter tenure to save on interest</li>
              <li>• Compare rates from multiple lenders</li>
              <li>• Consider prepayment options</li>
              <li>• Maintain good credit score for better rates</li>
            </ul>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              📊 EMI Components
            </h3>
            <p className="text-sm text-gray-600">
              EMI consists of principal and interest. Initially, more goes towards interest, 
              but over time, more of your EMI reduces the principal amount.
            </p>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              🚗 Loan Types
            </h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li>• Personal Loans: Unsecured, higher rates</li>
              <li>• Car Loans: Secured, lower rates</li>
              <li>• Home Loans: Long tenure, tax benefits</li>
              <li>• Education Loans: Special rates for students</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EMICalculator;
